// Planes Overhead (Oct 27, programming-plan.md Phase E; its own screen): airliners on their way into SFO right now,
// soonest first, from /api/planes (a Pages Function over adsb.lol's open ADS-B feed, the OpenSky Network when adsb.lol
// turns it away). Airline and aircraft type from
// the callsign and the transponder's type code; where each flight came from is left out, since the free route
// database (adsbdb) doesn't allow its routes to be republished. Business jets and private flights (mostly bound for
// Oakland or San Jose) are left out too. Polled once a minute; minutes count down from the fetch.
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import SnapshotDisplay from './snapshot-display.mjs';
import { time } from './format.mjs';

const POLL_MS = 60000;
const STALE_MS = 4 * 60000;
const MIN_PLANES = 2;

// ICAO airline prefixes seen at SFO
const AIRLINES = Object.fromEntries([
	['UAL', 'United'], ['ASA', 'Alaska'], ['SWA', 'Southwest'], ['AAL', 'American'], ['DAL', 'Delta'],
	['JBU', 'JetBlue'], ['SKW', 'SkyWest'], ['QXE', 'Horizon'], ['FFT', 'Frontier'], ['NKS', 'Spirit'],
	['HAL', 'Hawaiian'], ['SCX', 'Sun Country'], ['ACA', 'Air Canada'], ['WJA', 'WestJet'], ['AMX', 'Aeromexico'],
	['VOI', 'Volaris'], ['VIV', 'Viva'], ['AVA', 'Avianca'], ['CMP', 'Copa'], ['BAW', 'British'],
	['VIR', 'Virgin Atlantic'], ['DLH', 'Lufthansa'], ['AFR', 'Air France'], ['KLM', 'KLM'], ['SWR', 'Swiss'],
	['AUA', 'Austrian'], ['IBE', 'Iberia'], ['EIN', 'Aer Lingus'], ['SAS', 'SAS'], ['FIN', 'Finnair'], ['TAP', 'TAP'],
	['THY', 'Turkish'], ['UAE', 'Emirates'], ['QTR', 'Qatar'], ['ETD', 'Etihad'], ['ELY', 'El Al'],
	['AIC', 'Air India'], ['SIA', 'Singapore'], ['CPA', 'Cathay'], ['EVA', 'EVA Air'], ['CAL', 'China Airlines'],
	['JAL', 'JAL'], ['ANA', 'ANA'], ['KAL', 'Korean'], ['AAR', 'Asiana'], ['CCA', 'Air China'],
	['CES', 'China Eastern'], ['CSN', 'China Southern'], ['PAL', 'Philippine'], ['QFA', 'Qantas'],
	['ANZ', 'Air New Zealand'], ['FJI', 'Fiji'], ['TZP', 'Zipair'], ['STK', 'Starlux'], ['FDX', 'FedEx'],
	['UPS', 'UPS'], ['GTI', 'Atlas'], ['ABX', 'ABX'],
]);
// transponder type codes, as people say them
const TYPES = Object.fromEntries([
	['A319', 'A319'], ['A320', 'A320'], ['A20N', 'A320neo'], ['A321', 'A321'], ['A21N', 'A321neo'], ['A332', 'A330'],
	['A333', 'A330'], ['A339', 'A330neo'], ['A359', 'A350'], ['A35K', 'A350-1000'], ['A388', 'A380'], ['B712', '717'],
	['B737', '737-700'], ['B738', '737-800'], ['B739', '737-900'], ['B38M', '737 MAX 8'], ['B39M', '737 MAX 9'],
	['B752', '757'], ['B753', '757'], ['B763', '767'], ['B764', '767'], ['B772', '777'], ['B77L', '777'],
	['B77W', '777-300ER'], ['B788', '787-8'], ['B789', '787-9'], ['B78X', '787-10'], ['B744', '747'],
	['B748', '747-8'], ['BCS1', 'A220'], ['BCS3', 'A220'], ['E75L', 'E175'], ['E75S', 'E175'], ['E170', 'E170'],
	['E190', 'E190'], ['E195', 'E195'], ['CRJ2', 'CRJ'], ['CRJ7', 'CRJ-700'], ['CRJ9', 'CRJ-900'], ['DH8D', 'Q400'],
]);

let latest = null;
let display = null;
const poll = async () => {
	try {
		const response = await fetch('api/planes', { cache: 'no-store' });
		if (response.ok) latest = await response.json();
		// the loop checks a screen's data on its own refresh; check this one as the planes come in
		if (display?.weatherParameters && !display.active) display.getData(display.weatherParameters, true);
	} catch {
		// keep the last answer; it goes stale on its own
	}
};
// pulled from the loop Oct 27 (lineup `off`) until a source gives each flight's origin: then it doesn't poll either
if (!(window.RHL?.lineup?.off ?? []).includes('planes-overhead')) {
	poll();
	setInterval(poll, POLL_MS);
}

// the airliners still to land, minutes counted from when adsb.lol saw them
const inbound = (now) => {
	if (!latest?.at || now - new Date(latest.at) > STALE_MS) return [];
	const elapsed = Math.floor((now - new Date(latest.at)) / 60000);
	return (latest.arrivals ?? [])
		.filter((a) => AIRLINES[a.flight.slice(0, 3)])
		.map((a) => ({ ...a, eta: a.eta - elapsed }))
		.filter((a) => a.eta >= 1)
		.slice(0, 5);
};

class PlanesOverhead extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Over the Bay', { seconds: 14 });
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return inbound(new Date()).length >= MIN_PLANES;
	}

	// eslint-disable-next-line class-methods-use-this
	render(now) {
		const rows = inbound(now).map((a) => {
			const airline = AIRLINES[a.flight.slice(0, 3)];
			const type = TYPES[a.type] ?? a.type ?? '';
			return `<div class="row"><div class="t">${a.eta} min</div><div>${escapeHtml(`${airline} ${type}`.trim())}</div><div class="dim">${Math.round(a.alt / 100) * 100 > 0 ? `${(Math.round(a.alt / 100) * 100).toLocaleString('en-US')} ft` : ''}</div></div>`;
		});
		return `<div class="hdr">Arriving SFO, soonest first</div>
			<div class="list planes">${rows.join('')}</div>
			<div class="asof">ADS-B: ${latest.source === 'OpenSky' ? 'The OpenSky Network' : 'adsb.lol (ODbL)'} &middot; as of ${time(latest.at)}</div>`;
	}
}

display = new PlanesOverhead(35, 'planes-overhead');
registerDisplay(display);
